import React, { createContext, useContext, useReducer, useEffect } from "react";
import { USERMGMT_URL } from "../utils/envConfig";
import { touchSessionActivity, clearSessionActivity } from "../utils/sessionTimeout";
import { clearLowStockAlertSession } from "../utils/medicineLowStock";
import { clearPathLowStockAlertSession } from "../utils/pathLowStock";
import { useDispatch } from 'react-redux'; // Import useDispatch
import { loginWithOTP } from "../services/otpService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const LOGIN = "LOGIN";
const LOGOUT = "LOGOUT";

const organizationIdFromUser = (user, fallback = null) => {
  const raw = user?.organizationId ?? user?.organization_id ?? fallback;
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const initialState = {
  user: null,
  isAuthenticated: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case LOGIN:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
      };
    case LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const reduxDispatch = useDispatch(); // Initialize useDispatch

  const login = async (mobile, password, organizationId = null) => {
    try {
      // Pass organizationId to login API for validation BEFORE authentication
      // Backend will validate that user belongs to the selected organization
      const requestBody = { mobile, password };
      if (organizationId !== null && organizationId !== undefined) {
        requestBody.organizationId = organizationId;
      }
      
      const response = await fetch(`${USERMGMT_URL}/loginWithMobile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        console.error("Response status:", response.status); // Log the response status
        let errorMessage = "Login failed";
        try {
          const errorData = await response.json();
          errorMessage = errorData.msg || errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          // If response is not JSON, try to get text
          try {
            errorMessage = await response.text();
          } catch (e2) {
            // If that fails, use default message
          }
        }
        
        console.error("Login error details:", {
          status: response.status,
          message: errorMessage,
          mobile: mobile,
          hasOrganizationId: organizationId !== null
        });
        
        // Don't show alert - let the login page handle error display
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      const { token, user } = await response.json();

      // Map backend response to frontend format
      // Note: organizationId comes from user table (user management backend)
      // This is the source of truth - organizationId is validated in login/index.jsx
      const fakeUser = {
        id: user.id,
        username: user.userName || user.username,
        mobileNumber: user.mobile || user.mobileNumber || user.userName || user.username || null,
        roleName: user.role,
        description: "General department",
        // Use organizationId from user object (from user management service user table)
        // This will be validated in login/index.jsx to ensure it matches selected organization
        organizationId: organizationIdFromUser(user),
      };

      // Dispatch to context and save in local storage
      dispatch({ type: LOGIN, payload: fakeUser });
      reduxDispatch({ type: 'SET_USER', payload: fakeUser  }); // Dispatch to set user in Redux
      localStorage.setItem("user", JSON.stringify(fakeUser));
      localStorage.setItem("token", token);
      
      // Clear old organizationId from localStorage to prevent stale data
      // It will be set correctly in login/index.jsx after validation
      localStorage.removeItem("organizationId");
      
      // Only set organizationId if it's in the user object from user management service
      if (fakeUser.organizationId) {
        localStorage.setItem("organizationId", fakeUser.organizationId.toString());
      }

      touchSessionActivity();
      return fakeUser ; // Return the user object
      
    } catch (error) {
      console.error("Login error:", error.message);
      // Re-throw the error so it can be handled in the login page
      throw error;
    }
  };

  const loginWithOrgMobile = async (mobile, password, organizationId) => {
    try {
      // Validate required parameters
      if (!mobile || !password || !organizationId) {
        throw new Error("Mobile, password, and organizationId are required");
      }

      // Call the new loginWithOrgMobile endpoint
      const requestBody = { 
        mobile, 
        password, 
        organizationId: parseInt(organizationId) 
      };
      
      const response = await fetch(`${USERMGMT_URL}/loginWithOrgMobile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        console.error("Response status:", response.status);
        let errorMessage = "Login failed";
        try {
          const errorData = await response.json();
          errorMessage = errorData.msg || errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          try {
            errorMessage = await response.text();
          } catch (e2) {
            // If that fails, use default message
          }
        }
        
        console.error("LoginWithOrgMobile error details:", {
          status: response.status,
          message: errorMessage,
          mobile: mobile,
          organizationId: organizationId
        });
        
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      const { token, user } = await response.json();

      // Map backend response to frontend format
      const fakeUser = {
        id: user.id,
        username: user.userName || user.username,
        mobileNumber: user.mobile || user.mobileNumber || user.userName || user.username || null,
        roleName: user.role,
        description: "General department",
        organizationId: organizationIdFromUser(user, organizationId),
      };

      // Dispatch to context and save in local storage
      dispatch({ type: LOGIN, payload: fakeUser });
      reduxDispatch({ type: 'SET_USER', payload: fakeUser });
      localStorage.setItem("user", JSON.stringify(fakeUser));
      localStorage.setItem("token", token);
      
      // Clear old organizationId from localStorage to prevent stale data
      localStorage.removeItem("organizationId");
      
      // Set organizationId from the response or provided parameter
      const orgId = fakeUser.organizationId;
      if (orgId) {
        localStorage.setItem("organizationId", orgId.toString());
      }

      touchSessionActivity();
      return fakeUser;
      
    } catch (error) {
      console.error("LoginWithOrgMobile error:", error.message);
      throw error;
    }
  };

  const loginWithOTPCode = async (mobile, otp, organizationId = null) => {
    try {
      const { token, user } = await loginWithOTP(mobile, otp, organizationId);

      // Map backend response to frontend format
      // Note: organizationId comes from user table (user management backend)
      // This is the source of truth - organizationId is validated in login/index.jsx
      const fakeUser = {
        id: user.id,
        username: user.userName || user.username,
        mobileNumber: user.mobile || user.mobileNumber || user.userName || user.username || null,
        roleName: user.role || user.roleName,
        description: "General department",
        // Use organizationId from user object (from user management service user table)
        // This will be validated in login/index.jsx to ensure it matches selected organization
        organizationId: organizationIdFromUser(user),
      };

      // Dispatch to context and save in local storage
      dispatch({ type: LOGIN, payload: fakeUser });
      reduxDispatch({ type: 'SET_USER', payload: fakeUser });
      localStorage.setItem("user", JSON.stringify(fakeUser));
      localStorage.setItem("token", token);
      
      // Clear old organizationId from localStorage to prevent stale data
      // It will be set correctly in login/index.jsx after validation
      localStorage.removeItem("organizationId");
      
      // Only set organizationId if it's in the user object from user management service
      if (fakeUser.organizationId) {
        localStorage.setItem("organizationId", fakeUser.organizationId.toString());
      }

      touchSessionActivity();
      return fakeUser;
    } catch (error) {
      console.error("OTP login error:", error.message);
      throw error;
    }
  };

  const logout = () => {
    const userId = state.user?.id;
    dispatch({ type: LOGOUT });
    reduxDispatch({ type: 'CLEAR_USER' }); // Dispatch to clear user in Redux
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    // Clear organizationId on logout to prevent stale data from being used on next login
    localStorage.removeItem("organizationId");
    clearSessionActivity();
    clearLowStockAlertSession(userId);
    clearPathLowStockAlertSession(userId);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser );
      
      // Ensure base users have organizationId explicitly set to null (not undefined)
      // This helps components correctly detect base users
      if (user && (user.organizationId === undefined || (!user.hasOwnProperty('organizationId') && !localStorage.getItem("organizationId")))) {
        // Check if this is truly a base user (no organizationId in localStorage either)
        const orgIdFromStorage = localStorage.getItem("organizationId");
        if (!orgIdFromStorage) {
          // This is a base user - explicitly set organizationId to null
          user.organizationId = null;
          localStorage.setItem("user", JSON.stringify(user));
          console.log("[AuthContext] Base user detected - set organizationId to null");
        }
      }
      
      // If user has organizationId but no organizationName, try to fetch it (non-blocking)
      if (user?.organizationId && !user?.organizationName) {
        import("../utils/organizationUtils").then(({ fetchAndStoreOrganizationName }) => {
          fetchAndStoreOrganizationName(user.organizationId).then((orgName) => {
            if (orgName) {
              // Update user object with organization name
              const updatedUser = { ...user, organizationName: orgName };
              localStorage.setItem("user", JSON.stringify(updatedUser));
              dispatch({ type: LOGIN, payload: updatedUser });
              reduxDispatch({ type: 'SET_USER', payload: updatedUser });
            }
          }).catch((error) => {
            console.warn("[AuthContext] Could not fetch organization name:", error);
            // Continue with user object without organization name
          });
        });
      }
      dispatch({
        type: LOGIN,
        payload: user,
      });
      reduxDispatch({ type: 'SET_USER', payload: user }); // Set user in Redux
      touchSessionActivity();
    } else {
      dispatch({ type: LOGOUT });
    }

    // Listen for organizationId updates from login flow
    const handleOrganizationIdUpdate = (event) => {
      const { updatedUser } = event.detail;
      if (updatedUser) {
        console.log("[AuthContext] OrganizationId updated event received, updating user object");
        dispatch({ type: LOGIN, payload: updatedUser });
        reduxDispatch({ type: 'SET_USER', payload: updatedUser });
        // Also update localStorage to ensure consistency
        localStorage.setItem("user", JSON.stringify(updatedUser));
      } else {
        // If no updatedUser in event, try to read from localStorage (for super_admin case)
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser?.organizationId) {
              console.log("[AuthContext] OrganizationId update event received, reading from localStorage");
              dispatch({ type: LOGIN, payload: parsedUser });
              reduxDispatch({ type: 'SET_USER', payload: parsedUser });
            }
          } catch (e) {
            console.error("[AuthContext] Error parsing user from localStorage:", e);
          }
        }
      }
    };

    window.addEventListener('userOrganizationIdUpdated', handleOrganizationIdUpdate);

    const handleUserUpdated = (event) => {
      const { updatedUser } = event.detail || {};
      if (updatedUser) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
        dispatch({ type: LOGIN, payload: updatedUser });
        reduxDispatch({ type: 'SET_USER', payload: updatedUser });
      }
    };

    window.addEventListener('userUpdated', handleUserUpdated);

    return () => {
      window.removeEventListener('userOrganizationIdUpdated', handleOrganizationIdUpdate);
      window.removeEventListener('userUpdated', handleUserUpdated);
    };
  }, [reduxDispatch]); // Add reduxDispatch as a dependency

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        login,
        loginWithOrgMobile,
        loginWithOTP: loginWithOTPCode,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
